import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import ImageUploader from '../../components/ImageUploader';
import useUploadImages from '../../hooks/useUploadImages';
import useCreateHappyEnding from '../../hooks/useCreateHappyEndingEntry';
import usePetsList from '../../hooks/usePetsList';
import useUpdatePet from '../../hooks/useUpdatePet';

const showAlert = (type, message, method, timeout = 10000) => {
  const container = document.getElementById('contact_results_' + method);
  if (!container) return;

  container.innerHTML = `
    <br/>
    <div class="alert alert-${type}" role="alert">
      ${message}
    </div>
  `;

  setTimeout(() => {
    container.innerHTML = '';
  }, timeout);
};

const HappyEndingsEntry = () => {
  const [titleBS, setTitleBS] = useState('');
  const [titleEN, setTitleEN] = useState('');
  const [descriptionBS, setDescriptionBS] = useState('');
  const [descriptionEN, setDescriptionEN] = useState('');
  const [date, setDate] = useState('');
  const [petId, setPetId] = useState('');
  const [selectedPet, setSelectedPet] = useState(null);
  const [images, setImages] = useState([]);
  const [resetKey, setResetKey] = useState(0);
  const [adminPassword, setAdminPassword] = useState('');

  const createHappyEndingMutation = useCreateHappyEnding();
  const uploadImagesMutation = useUploadImages();
  const updatePetMutation = useUpdatePet();
  const { data: pets = [], isLoading: petsLoading } = usePetsList();

  const petOptions = useMemo(() => {
    return [...pets]
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(pet => ({
        value: pet._id,
        label: `${pet.name} (${pet.status || 'available'})`,
      }));
  }, [pets]);

  const handleUpload = newFiles => {
    setImages(prev => {
      const merged = [...prev, ...newFiles];

      // dedupe po name+size kombinaciji
      const unique = Array.from(
        new Map(merged.map(file => [file.name + file.size, file])).values()
      );

      console.log('📁 Unique images:', unique);
      return unique;
    });
  };

  const handleRemoveImage = removedFile =>
    setImages(prev => prev.filter(img => img !== removedFile));

  const handleSubmit = async e => {
    e.preventDefault();
    const form = e.target;

    if (!titleBS || !titleEN || !descriptionBS || !descriptionEN || !date || !adminPassword) {
      showAlert('danger', 'Sva polja su obavezna!', 'create');
      return;
    }

    // Normalizacija naslova: lowercase, space -> dash, uklanjanje specijalnih znakova
    const normalizeTitle = title => {
      return title
        .trim() // ukloni praznine sa krajeva
        .toLowerCase() // lowercase
        .replace(/\s+/g, '-') // zamijeni space sa -
        .replace(/[^\w-]/g, ''); // ukloni sve osim slova, brojeva i -
    };

    const folderPath = `happyendings/${normalizeTitle(titleBS)}`;

    // 1️⃣ Prvo upload slika
    let uploadedImages = { urls: [] };
    if (images.length > 0) {
      try {
        uploadedImages = await uploadImagesMutation.mutateAsync({
          files: images,
          folder: folderPath,
        });
        showAlert('success', 'Slike uspješno unesene!', 'upload');
      } catch (uploadErr) {
        console.error(uploadErr);
        showAlert('danger', 'Greška pri uploadu slika!', 'upload');
        return; // Stop ako upload nije uspio
      }
    }

    // 3️⃣ Mapiraj url-ove u objekat { src, title }
    const imagesPayload = uploadedImages.urls.map(url => ({
      src: url,
      title: null,
    }));

    // 2️⃣ Kreiraj dokument u bazi sa URL-ovima slika (bez pet_id — happy_endings schema ne dozvoljava extra polja)
    const payload = {
      title_bs: titleBS,
      title_en: titleEN,
      description_bs: descriptionBS,
      description_en: descriptionEN,
      date: new Date(date),
      images: imagesPayload,
      adminPassword,
    };

    try {
      const result = await createHappyEndingMutation.mutateAsync(payload);
      showAlert('success', 'Post uspješno unesen!', 'create');

      // 3️⃣ Ako je odabran ljubimac, ažurirati status na 'adopted' i postaviti happy_ending_id
      if (petId) {
        try {
          await updatePetMutation.mutateAsync({
            id: petId,
            data: {
              status: 'adopted',
              happy_ending_id: result.id,
              adminPassword,
            },
          });
        } catch (petErr) {
          console.error('Pet update failed:', petErr);
          showAlert('warning', 'Post kreiran, ali greška pri ažuriranju ljubimca.', 'create');
        }
      }

      // Reset forme
      setTitleBS('');
      setTitleEN('');
      setDescriptionBS('');
      setDescriptionEN('');
      setDate('');
      setPetId('');
      setSelectedPet(null);
      setImages([]);
      setAdminPassword('');
      setResetKey(prev => prev + 1);
    } catch (err) {
      console.error(err);
      showAlert('danger', 'Greška pri unosu posta!', 'create');
    }
  };

  return (
    <div className="happy-ending-entry col-lg-12">
      <div className="paws-house-bg1 bg-light-custom element-section">
        <div className="contact-info col-lg-8">
          <h4>Unos Happy Ending posta</h4>
          <form className="form-group" onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <label>
                  Naslov (BS) <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={titleBS}
                  onChange={e => setTitleBS(e.target.value)}
                  className="form-control"
                  required
                />
              </div>

              <div className="col-md-6">
                <label>
                  Naslov (EN) <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={titleEN}
                  onChange={e => setTitleEN(e.target.value)}
                  className="form-control"
                  required
                />
              </div>

              <div className="col-md-12">
                <label>
                  Opis (BS) <span className="required">*</span>
                </label>
                <textarea
                  value={descriptionBS}
                  onChange={e => setDescriptionBS(e.target.value)}
                  className="form-control"
                  rows="5"
                  style={{
                    borderRadius: 0,
                  }}
                  required
                />
              </div>

              <div className="col-md-12">
                <label>
                  Opis (EN) <span className="required">*</span>
                </label>
                <textarea
                  value={descriptionEN}
                  onChange={e => setDescriptionEN(e.target.value)}
                  className="form-control"
                  rows="5"
                  style={{
                    borderRadius: 0,
                  }}
                  required
                />
              </div>

              <div className="col-md-6">
                <label>
                  Datum <span className="required">*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className="form-control"
                  required
                />
              </div>

              <div className="col-md-6">
                <label>
                  Ljubimac <span style={{ color: '#999', fontWeight: 'normal' }}>(opcionalno)</span>
                </label>
                <Select
                  options={petOptions}
                  value={selectedPet}
                  onChange={option => {
                    setSelectedPet(option);
                    setPetId(option ? option.value : '');
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Pretraži i odaberi ljubimca..."
                  isClearable
                  isLoading={petsLoading}
                />
              </div>

              <div className="col-md-12" style={{ marginTop: '20px' }}>
                <label>Slike</label>
                <ImageUploader
                  onUpload={handleUpload}
                  onRemove={handleRemoveImage}
                  resetKey={resetKey}
                />
              </div>
            </div>

            {/* Hidden Username to satisfy Chrome Password Manager heuristics */}
            <input
              type="text"
              name="username"
              value="admin"
              autoComplete="username"
              style={{ display: 'none' }}
              readOnly
            />

            <div className="col-md-6">
              <label htmlFor="adminPassword">
                Admin Password<span className="required">*</span>
              </label>
              <input
                type="password"
                id="adminPassword"
                name="adminPassword"
                className="form-control input-field"
                autoComplete="current-password"
                required
                onChange={e => setAdminPassword(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-primary mt-3">
              Unesi
            </button>
          </form>

          <div id="contact_results_create"></div>
          <div id="contact_results_upload"></div>
        </div>
      </div>
    </div>
  );
};

export default HappyEndingsEntry;
